import React, { useMemo } from 'react';
import { useQuery } from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import { Col, Divider, Row } from 'antd';
import {
  Link,
  Currency,
  InfoTooltip,
  LoadingState,
  Text,
  Title,
  Gutter,
  Gap,
} from '@common/components';
import { Colors } from 'works/legacy/lib/style-helper';
import { StaffingRequestState } from 'works/components/staffing-request-state/staffing-request-state';
import { EffectiveBillingCardQuery } from 'works/graphql/effective-billing-card';
import { toUSFormat, toYearMonthDay } from 'works/legacy/lib/date-helper';
import {
  getBillingCardRateByShiftType,
  roleLevelParser,
} from '@institutions/services';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { humanizeBoolean } from '@common/services';
import { StaffingRequestDetailsDrawerType } from '@jobs/graphql';
import { ShiftTypeWithIcon } from '@jobs/components/shift-type-with-icons';
import Auth from 'works/legacy/lib/auth';

export function StaffingRequestDetailsContent({
  staffingRequest,
}: {
  staffingRequest?: StaffingRequestDetailsDrawerType;
}) {
  const {
    data: billingCardData,
    loading: billingCardLoading,
    error: billingCardError,
  } = useQuery(EffectiveBillingCardQuery, {
    skip: !staffingRequest,
    variables: {
      effectiveDate: toYearMonthDay(staffingRequest?.startDate),
      organizationUnitId: staffingRequest?.organizationUnit.id,
      roleId: staffingRequest?.role.id,
      roleLevel: staffingRequest?.roleLevel,
    },
  });

  const isSupplierUser = Auth.supplierMember();
  const isOrganizationUser = Auth.organizationMember();

  // It's fine to not use ErrorHandler in this query.
  // Ex.: Supplier users are not authorized to see billing cards,
  // which is an expected error but they can see the rest of the content.
  if (billingCardError) {
    console.warn('StaffingRequestDetailsContent:', billingCardError);
  }

  const currentMembership = useCurrentMembership();
  const showComplianceListLink = useMemo(
    () => currentMembership.canEditIndividualComplianceList,
    [currentMembership]
  );

  if (billingCardLoading) {
    return <LoadingState />;
  }

  const specialties = staffingRequest?.staffingRequestSpecialties?.map(
    (specialtyAssoc) => ({
      name: specialtyAssoc.clinicalSpecialty.displayName,
      necessityLevel: specialtyAssoc.necessityLevel,
    })
  );

  const technologies = staffingRequest?.staffingRequestTechnologies?.map(
    (technologyAssoc) => ({ name: technologyAssoc.technology.name })
  );

  const certifications = staffingRequest?.staffingRequestCertifications?.map(
    (certificationAssoc) => ({
      credential: certificationAssoc.certification.credential,
      necessityLevel: certificationAssoc.necessityLevel,
    })
  );

  const previousValues = staffingRequest?.lastChangedValues;

  const billingCardRateByShiftType = getBillingCardRateByShiftType(
    staffingRequest?.billingCard,
    staffingRequest?.shiftType
  );

  const suggestedBillingCardRateByShiftType = getBillingCardRateByShiftType(
    billingCardData?.effectiveBillingCard,
    staffingRequest?.shiftType
  );

  const hasBillingCard = !!staffingRequest?.billingCard;

  return (
    <div className="template-details">
      <Gutter direction="bottom">
        <Title size="h4">Position Details</Title>
      </Gutter>
      <Row align="middle" gutter={[4, 4]}>
        <Col span={8}>
          <Text secondary>Status</Text>
        </Col>
        <Col span={16}>
          <Text bold>
            <StaffingRequestState state={staffingRequest?.state ?? ''} />
          </Text>
        </Col>
        <Col span={8}>
          <Text secondary>ID</Text>
        </Col>
        <Col span={16}>
          <Text bold>{staffingRequest?.code}</Text>
        </Col>
        <Col span={8}>
          <Text secondary>Name</Text>
        </Col>
        <Col span={16}>
          <Text bold>{staffingRequest?.name}</Text>
        </Col>
        <Col span={8}>
          <Text secondary>Organization</Text>
        </Col>
        <Col span={16}>{staffingRequest?.organization.name}</Col>
        <Col span={8}>
          <Text secondary>Facility</Text>
        </Col>
        <Col span={16}>
          {
            staffingRequest?.organizationUnit.department?.organizationBusiness
              ?.name
          }
        </Col>

        <Col span={8}>
          <Text secondary>Unit</Text>
        </Col>
        <Col span={16}>
          {staffingRequest?.organizationUnit.clinicalUnit?.displayName}
        </Col>

        <Col span={8}>
          <Text secondary>Cost Center</Text>
        </Col>
        <Col span={16}>{staffingRequest?.organizationUnit.costCenter}</Col>

        <Col span={8}>
          <Text secondary>External Job ID</Text>
        </Col>
        <Col span={16}>{staffingRequest?.externalId}</Col>

        <Col span={8}>
          <Text secondary>Hiring Leader</Text>
        </Col>
        <Col span={16} className="sentry-mask">
          <ChangedValues
            previousValue={previousValues?.hiringLeader?.name}
            currentValue={staffingRequest?.hiringLeader?.name}
          />
        </Col>

        <Col span={8}>
          <Text secondary>Role</Text>
        </Col>
        <Col span={16}>{staffingRequest?.role.abbreviation}</Col>

        <Col span={8}>
          <Text secondary>Level</Text>
        </Col>
        <Col span={16}>{roleLevelParser(staffingRequest?.roleLevel)}</Col>

        <Col span={8}>
          <Text secondary>Open Positions</Text>
        </Col>
        <Col span={16}>
          <ChangedValues
            previousValue={previousValues?.openStaffingRequestPositionsCount}
            currentValue={staffingRequest?.openStaffingRequestPositionsCount}
          />
        </Col>

        <Col span={8}>
          <Text secondary>Total Positions</Text>
        </Col>
        <Col span={16}>
          <ChangedValues
            previousValue={previousValues?.staffingRequestPositionsCount}
            currentValue={staffingRequest?.staffingRequestPositionsCount}
          />
        </Col>

        <Col span={8}>
          <Text secondary>International Candidate</Text>
        </Col>
        <Col span={16}>
          <ChangedValues
            previousValue={humanizeBoolean(previousValues?.international)}
            currentValue={humanizeBoolean(staffingRequest?.international)}
          />
        </Col>

        <Col span={8}>
          <Text secondary>Start Date</Text>
        </Col>
        <Col span={16}>
          <ChangedValues
            previousValue={
              previousValues?.startDate && toUSFormat(previousValues?.startDate)
            }
            currentValue={toUSFormat(staffingRequest?.startDate)}
          />
        </Col>

        <Col span={8}>
          <Text secondary>Length</Text>
        </Col>
        <Col span={16}>
          <ChangedValues
            previousValue={weeksFormat(previousValues?.length)}
            currentValue={weeksFormat(staffingRequest?.length)}
          />
        </Col>

        <Col span={8}>
          <Text secondary>Compliance List</Text>
        </Col>
        <Col span={16}>
          {showComplianceListLink && (
            <Link
              to={`/works/admin/templates/compliance-list/${staffingRequest?.worksListTemplate.id}/items`}
            >
              {staffingRequest?.worksListTemplate.name}
            </Link>
          )}

          {!showComplianceListLink && staffingRequest?.worksListTemplate.name}
        </Col>
      </Row>

      <Divider />

      <Gutter direction="bottom">
        <Title size="h4">Shift Info</Title>
      </Gutter>
      <Row gutter={[4, 4]}>
        <Col span={8}>
          <Text secondary>Shift</Text>
        </Col>
        <Col span={16}>
          <ChangedValues
            previousValue={
              previousValues?.shiftType && (
                <ShiftTypeWithIcon value={previousValues?.shiftType} disabled />
              )
            }
            currentValue={
              staffingRequest?.shiftType && (
                <ShiftTypeWithIcon value={staffingRequest?.shiftType} />
              )
            }
          />
        </Col>

        <Col span={8}>
          <Text secondary>Shifts per week</Text>
        </Col>
        <Col span={16}>
          <ChangedValues
            previousValue={previousValues?.shiftsPerWeek}
            currentValue={staffingRequest?.shiftsPerWeek}
          />
        </Col>

        <Col span={8}>
          <Text secondary>Hours per shift</Text>
        </Col>
        <Col span={16}>
          <ChangedValues
            previousValue={previousValues?.hoursPerShift}
            currentValue={staffingRequest?.hoursPerShift}
          />
        </Col>
      </Row>

      <Divider />

      <Gutter direction="bottom">
        <Title size="h4">Rate Info</Title>
      </Gutter>
      <Row gutter={[4, 4]}>
        {(isOrganizationUser || (!hasBillingCard && isSupplierUser)) && (
          <>
            <Col span={8}>
              <Text secondary>{isSupplierUser ? 'Rate' : 'Base Template'}</Text>
            </Col>
            <Col span={16}>
              {hasBillingCard ? (
                <Text deleted secondary>
                  <Currency value={staffingRequest?.billingRegularRate || 0} />
                </Text>
              ) : (
                <ChangedValues
                  previousValue={moneyFormat(
                    previousValues?.billingRegularRate
                  )}
                  currentValue={moneyFormat(
                    staffingRequest?.billingRegularRate
                  )}
                />
              )}
            </Col>
          </>
        )}

        {(suggestedBillingCardRateByShiftType ||
          billingCardRateByShiftType) && (
          <>
            <Col span={8}>
              <Text secondary>Traveler</Text>
            </Col>
            <Col span={16}>
              <BillingCardRateFormat
                hasBillingCard={hasBillingCard}
                rate={billingCardRateByShiftType?.travelerRate}
                suggestedRate={
                  suggestedBillingCardRateByShiftType?.travelerRate
                }
              />
            </Col>

            <Col span={8}>
              <Text secondary>Local</Text>
            </Col>
            <Col span={16}>
              <BillingCardRateFormat
                hasBillingCard={hasBillingCard}
                rate={billingCardRateByShiftType?.localRate}
                suggestedRate={suggestedBillingCardRateByShiftType?.localRate}
              />
            </Col>

            <Col span={8}>
              <Text secondary>Direct Sourcing</Text>
            </Col>
            <Col span={16}>
              <BillingCardRateFormat
                hasBillingCard={hasBillingCard}
                rate={billingCardRateByShiftType?.directSourcingRate}
                suggestedRate={
                  suggestedBillingCardRateByShiftType?.directSourcingRate
                }
              />
            </Col>
          </>
        )}

        <Col span={8}>
          <Text secondary>Estimated Cost</Text>
        </Col>
        <Col span={16}>
          {hasBillingCard ? (
            staffingRequest?.lowestEstimatedCost ===
            staffingRequest?.highestEstimatedCost ? (
              <Currency value={staffingRequest?.lowestEstimatedCost} />
            ) : (
              <>
                <Currency value={staffingRequest?.lowestEstimatedCost || 0} /> -{' '}
                <Currency value={staffingRequest?.highestEstimatedCost || 0} />
              </>
            )
          ) : (
            <ChangedValues
              previousValue={moneyFormat(previousValues?.estimatedCost)}
              currentValue={moneyFormat(staffingRequest?.estimatedCost)}
            />
          )}
        </Col>
      </Row>

      <Divider />

      <Gutter direction="bottom">
        <Title size="h4">Qualification</Title>
      </Gutter>
      <Row gutter={[4, 4]}>
        <Col span={8}>
          <Text secondary>Years of Experience</Text>
        </Col>
        <Col span={16}>{staffingRequest?.yearsOfExperienceHumanize}</Col>

        <Col span={8}>
          <Text secondary>Specialties</Text>
        </Col>
        <Col span={16} className="multi-line-description-list">
          {specialties?.map(({ name, necessityLevel }) => (
            <React.Fragment key={name}>
              <Text>{name} </Text>
              <Text secondary>({necessityLevel})</Text>
              <br />{' '}
            </React.Fragment>
          ))}
        </Col>

        <Col span={8}>
          <Text secondary>Technologies</Text>
        </Col>
        <Col span={16}>
          {technologies?.map(({ name }) => (
            <React.Fragment key={name}>
              {name} <br />{' '}
            </React.Fragment>
          ))}
        </Col>

        <Col span={8}>
          <Text secondary>Certifications</Text>
        </Col>
        <Col span={16}>
          {certifications?.map(({ credential, necessityLevel }) => (
            <React.Fragment key={credential}>
              <Text>{credential} </Text>
              <Text secondary>({necessityLevel})</Text>
              <br />{' '}
            </React.Fragment>
          ))}
        </Col>
      </Row>

      <Divider />

      <Gutter direction="bottom">
        <Title size="h4">Description</Title>
      </Gutter>
      <Row gutter={[4, 4]}>
        <Col>
          <Text>{staffingRequest?.description}</Text>
        </Col>
      </Row>
    </div>
  );
}

function weeksFormat(value: number | undefined | null) {
  if (!value && value !== 0) return undefined;
  return `${value} wks`;
}

function moneyFormat(value: number | undefined) {
  if (!value) return '';
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

function BillingCardRateFormat({
  hasBillingCard,
  rate,
  suggestedRate,
}: {
  hasBillingCard: boolean;
  rate: number | undefined;
  suggestedRate: number | undefined;
}) {
  if (hasBillingCard) {
    return <Currency value={rate || 0} />;
  }

  return (
    <>
      <Text deleted secondary>
        <Currency value={suggestedRate || 0} />
      </Text>
      <InfoTooltip
        size="1x"
        leftOffset={4}
        title="This Staffing Request’s rate is different from the rate card’s rate."
        icon={faTriangleExclamation}
        iconColor={Colors.sunDarker}
      />
    </>
  );
}

function ChangedValues({
  previousValue,
  currentValue,
}: {
  previousValue?: React.ReactNode;
  currentValue?: React.ReactNode;
}) {
  return (
    <>
      {previousValue && previousValue !== currentValue ? (
        <Gap>
          <Text color="grassMid" weight="bold">
            {currentValue}
          </Text>
          <Text deleted secondary>
            {previousValue}
          </Text>
        </Gap>
      ) : (
        <Text>{currentValue}</Text>
      )}
    </>
  );
}
